import React, { useState } from 'react';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { saveStudentLeadInfo } from '../../Redux/Redux-slices/partnerInfoSlice';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../contextApi/NotificationProvider';

const ModalFormStudent = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { loading, success, error } = useSelector((state) => state.partner);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    city: '',
    email: '',
    mobile: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10,15}$/;

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (formData.city && formData.city.length > 50) {
      newErrors.city = 'City must be less than 50 characters';
    }

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.mobile && !mobileRegex.test(formData.mobile)) {
      newErrors.mobile = 'Please enter a valid mobile number';
    }

    if (formData.message && formData.message.length > 500) {
      newErrors.message = 'Message must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    // setLoading(true);

    const payload = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      email: formData.email,
      ...(formData.city && { city: formData.city }),
      ...(formData.mobile && { mobile: formData.mobile }),
      ...(formData.message && { message: formData.message }),
    };

    try {
      const resultAction = await dispatch(saveStudentLeadInfo(payload));

      if (resultAction.meta.requestStatus === "fulfilled") {
        const message =
          resultAction.payload?.message ||
          "Your request was submitted successfully.";

        if (message === "Your request is already submitted.") {
          // Don't navigate — just show the message as an error
          showNotification(message, "error", 8000);
          onClose()
        } else {
          // Success case
          showNotification(message, "success", 8000);
          navigate("/student-placement-cell/thank-you");
          setFormData("");
          onClose();
        }
      } else {
        const errorMsg =
          resultAction.payload?.message || "Something went wrong.";
        showNotification(errorMsg, "error", 8000);
        setFormData("");
      }
    } catch (err) {
      showNotification("Failed to submit.", "error", 8000);
    } finally {
      // setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-violet-100">
        <div className="relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-black  transition-colors duration-200"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-8">
          {submitSuccess ? (
            <div className="text-center py-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-violet-100 mb-6">
                <svg className="h-10 w-10 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-black mb-2">Thank You!</h3>
              <p className="text-black mb-6">Your form has been submitted successfully.</p>
              <button
                onClick={() => {
                  setSubmitSuccess(false);
                  onClose();
                }}
                className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-all duration-200 shadow hover:shadow-md"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="text-center mb-2">
                <h2 className="text-3xl font-bold text-black">Contact Us</h2>
                <p className="text-black mt-2">Fill out the form below</p>
              </div>

              {errors.api && (
                <div className="mb-6 p-4 bg-red-100 text-red-700 rounded-lg border border-red-200">
                  {errors.api}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-black mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.first_name ? 'border-red-500' : 'border-violet-200'}`}
                    placeholder="John"
                  />
                  {errors.first_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.first_name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-black mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.last_name ? 'border-red-500' : 'border-violet-200'}`}
                    placeholder="Doe"
                  />
                  {errors.last_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.last_name}</p>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-black mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.email ? 'border-red-500' : 'border-violet-200'}`}
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-black mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.city ? 'border-red-500' : 'border-violet-200'}`}
                    placeholder="New York"
                  />
                  {errors.city && (
                    <p className="mt-2 text-sm text-red-600">{errors.city}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="mobile" className="block text-sm font-medium text-black mb-2">
                    Mobile
                  </label>
                  <input
                    type="tel"
                    id="mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.mobile ? 'border-red-500' : 'border-violet-200'}`}
                    placeholder="1234567890"
                  />
                  {errors.mobile && (
                    <p className="mt-2 text-sm text-red-600">{errors.mobile}</p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-black mb-2">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows="2"
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-1.5 border rounded-lg focus:ring-2 focus:ring-violet-300 focus:border-violet-500 outline-none transition-all ${errors.message ? 'border-red-500' : 'border-violet-200'}`}
                  placeholder="Your message here..."
                ></textarea>
                {errors.message && (
                  <p className="mt-2 text-sm text-red-600">{errors.message}</p>
                )}
              </div>

              <div className="flex flex-col space-y-3">
                <button
                  type="submit"
                  className="w-full px-6 py-1.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow hover:shadow-md mx-auto"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </span>
                  ) : 'Submit'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalFormStudent;